'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import useFetchProjectsProcurement from '../../hooks/useFetchProjectsProcurement';
import RfqDataEntryModal from './Modals/RfqDataEntryModal';
import PreflightRfqModal from './Modals/PreflightRfqModal';
import getPORequestColumns from '../../utils/getPORequestColumns';
import useUserMap from '../../hooks/useUserMap';
import RfqBidSubmissionReviewModal from './Modals/RfqBidSubmissionReviewModal';
import ConfirmDialog from '../UserDialog/ConfirmDialog';
import axios from 'axios';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
]);

const OpenRequests = ({ data, refetch }) => {
  const { projects } = useFetchProjectsProcurement();
  const [editingRfq, setEditingRfq] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const userMap = useUserMap();

  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);

  const [viewBidRfqId, setViewBidRfqId] = useState(null);
  const [viewBidModalOpen, setViewBidModalOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  const [newRequest, setNewRequest] = useState({
    name: '',
    description: '',
    vendorInstructions: '',
    deliveryLocation: '',
    workScope: '',
    formType: 'Equipment',
    proposalDueDate: null,
    desiredDeliveryDate: null,
    baseProjectId: '',
    baseSubsidiaryId: '',
    vendor: null,
    enabledForBidding: false,
  });

  useEffect(() => {
    setRowData(data || []);
  }, [data]);

  useEffect(() => {
    if (editingRfq) {
      setNewRequest({
        name: editingRfq.name || '',
        description: editingRfq.description || '',
        vendorInstructions: editingRfq.vendorInstructions || '',
        deliveryLocation: editingRfq.deliveryLocation || '',
        workScope: editingRfq.workScope || '',
        formType: editingRfq.formType || 'Equipment',
        proposalDueDate: editingRfq.proposalDueDate || new Date().toISOString(),
        desiredDeliveryDate: editingRfq.desiredDeliveryDate || null,
        baseProjectId: editingRfq.baseProjectId || '',
        baseSubsidiaryId: editingRfq.baseSubsidiaryId || '',
        vendor: null,
        enabledForBidding: editingRfq.enabledForBidding || false,
      });
    }
  }, [editingRfq]);

  const actionsRenderer = (params) => (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
      <Tooltip title="Edit Request">
        <IconButton size="small" color="primary" onClick={() => handleEditLineItems(params.data)}>
          <SettingsIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Archive Request">
        <IconButton size="small" color="error" onClick={() => handleDeleteRfq(params.data)}>
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const handleUpdateRfq = async (updatedHeader) => {
    // const isNew = !editingRfq;

    // if (isNew) {
    //   try {
    //     const refreshedData = await fetchProcurementRequests();
    //     setRowData(refreshedData.open);
    //   } catch (err) {
    //     console.error('Failed to refresh after creating RFQ:', err);
    //     toast.error('Could not refresh the table after creating the RFQ.');
    //   }
    // } else {
    //   // If it's no longer "open", remove it
    //   if (updatedHeader.readyForApproval || updatedHeader.formStatus) {
    //     setRowData((prev) => prev.filter((rfq) => rfq.id !== updatedHeader.id));
    //   } else {
    //     setRowData((prev) => prev.map((rfq) => (rfq.id === updatedHeader.id ? { ...rfq, ...updatedHeader } : rfq)));
    //   }
    // }
    await refetch();
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false });
  };

  const handleDeleteRfq = (rfqRow) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to archive "${rfqRow.name}"?`,
      onConfirm: async () => {
        try {
          await axios.put('/api/RfqHeader/rfq-isdelete', { id: rfqRow.id, isDeleted: true });
          // only runs if status is 2xx
          toast.success('Request archived successfully');
          //setRowData((prev) => prev.filter((r) => r.id !== rfqRow.id));
          await refetch();
        } catch (err) {
          console.error(err);
          if (err.response?.status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Error archiving the request');
          }
        } finally {
          closeConfirmDialog();
        }
      },
      onCancel: closeConfirmDialog,
    });
  };

  const handleEditLineItems = async (poRequest) => {
    setEditingRfq(poRequest); // pass the entire request object
    setOpenDialog(true);
  };

  const columnDefs = useMemo(() => getPORequestColumns(userMap, actionsRenderer), [userMap]);

  const handleOpenDialog = () => setPreflightOpen(true);

  const handlePreflightComplete = (result) => {
    setPreflightResult(result);
    setNewRequest({
      name: result.rfqName,
      description: '',
      vendorInstructions: '',
      deliveryLocation: '',
      workScope: '',
      formType: 'Equipment',
      proposalDueDate: null,
      desiredDeliveryDate: null,
      baseProjectId: result.baseProjectId,
      baseSubsidiaryId: result.baseSubsidiaryId,
      vendor: null,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => {
      setEditingRfq(null);
      setNewRequest({
        name: '',
        description: '',
        vendorInstructions: '',
        deliveryLocation: '',
        workScope: '',
        formType: 'Equipment',
        proposalDueDate: null,
        desiredDeliveryDate: null,
        baseProjectId: '',
        baseSubsidiaryId: '',
        vendor: null,
        enabledForBidding: false,
      });
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRequest((prev) => ({ ...prev, [name]: value }));
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        padding: 2,
      }}
    >
      <>
        <Box align="left" sx={{ mb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleOpenDialog}>
            New Request
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <div style={{ height: '100%', width: '100%' }}>
            <AgGridReact columnDefs={columnDefs} defaultColDef={defaultColDef} rowData={rowData} pagination={true} />
          </div>
        </Box>
      </>

      {/* Modals */}
      {openDialog && (
        <RfqDataEntryModal
          key={editingRfq?.id ?? 'new'}
          open={true}
          onClose={handleCloseDialog}
          newRequest={editingRfq || newRequest}
          handleInputChange={handleInputChange}
          rfqId={editingRfq?.id}
          onUpdateRfq={handleUpdateRfq}
          enabledForBidding={editingRfq?.enabledForBidding}
          onViewBids={(rfqId) => {
            setOpenDialog(false);
            setTimeout(() => {
              setViewBidRfqId(rfqId);
              setViewBidModalOpen(true);
            }, 250);
          }}
          preflight={editingRfq ? null : preflightResult}
        />
      )}
      {preflightOpen && (
        <PreflightRfqModal
          open={preflightOpen}
          onClose={() => setPreflightOpen(false)}
          onConfirm={handlePreflightComplete}
          projects={projects}
        />
      )}

      <RfqBidSubmissionReviewModal
        open={viewBidModalOpen}
        onClose={() => {
          setViewBidModalOpen(false);
          setViewBidRfqId(null);
        }}
        rfqId={viewBidRfqId}
        onSubmitForApproval={(rfqId) => {
          setViewBidModalOpen(false);
          setViewBidRfqId(null);
          handleUpdateRfq({ id: rfqId, readyForApproval: true });
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        onClose={closeConfirmDialog}
      />
    </Box>
  );
};

export default OpenRequests;
